// VIDEO-EDITING — Django REST API client for the project artifact.
//
// Mirrors `/api/videos/projects/{id}/` (GET, PATCH, soft-delete via
// `/delete/`) and `/api/videos/broll-asset/` (POST multipart). All
// responses are Zod-validated at the boundary per frontend/CLAUDE.md
// Gotcha #2 — the legacy CaptionEditor cast network bodies straight to
// the runtime interface, which silently swallowed schema drift.
import { z } from "zod";

import { apiFetch } from "@/lib/jwt-auth-client";

import {
  VideoProjectSchema,
  type VideoProject,
} from "../types/project";

const BrollAssetSchema = z.object({
  url: z.string(),
  storageKey: z.string(),
  mimeType: z.string(),
  durationSec: z.number(),
});

export type BrollAsset = z.infer<typeof BrollAssetSchema>;

async function jsonOrThrow<T>(res: Response, schema: z.ZodType<T>): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      (body as { error?: string }).error ?? `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const data: unknown = await res.json();
  return schema.parse(data);
}

export async function getProject(
  projectId: string,
  signal?: AbortSignal,
): Promise<VideoProject> {
  const res = await apiFetch(
    `/api/videos/projects/${encodeURIComponent(projectId)}/`,
    { signal },
  );
  return jsonOrThrow(res, VideoProjectSchema);
}

export async function patchProject(
  projectId: string,
  patch: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<VideoProject> {
  const res = await apiFetch(
    `/api/videos/projects/${encodeURIComponent(projectId)}/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
      signal,
    },
  );
  return jsonOrThrow(res, VideoProjectSchema);
}

export async function deleteProject(
  projectId: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await apiFetch(
    `/api/videos/projects/${encodeURIComponent(projectId)}/delete/`,
    { method: "POST", signal },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      (body as { error?: string }).error ?? `Delete failed (${res.status})`;
    throw new Error(msg);
  }
}

export async function uploadBrollAsset(
  file: File,
  durationSec: number,
  signal?: AbortSignal,
): Promise<BrollAsset> {
  const form = new FormData();
  form.append("file", file);
  form.append("durationSec", String(durationSec));
  const res = await apiFetch("/api/videos/broll-asset/", {
    method: "POST",
    body: form,
    signal,
  });
  return jsonOrThrow(res, BrollAssetSchema);
}

// React Query key namespace — features that call into the editor use
// these to invalidate on save/translate/export-result.
export const videoProjectKeys = {
  all: ["video-project"] as const,
  detail: (projectId: string) =>
    ["video-project", "detail", projectId] as const,
};
